import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import mapboxgl from 'mapbox-gl';
import { MapAnchor, milesBetween } from 'src/utils/mapViewport';
import { useFormatDistance } from 'src/hooks/useMarket';
import { anchorCaption } from 'src/utils/mapPin.util';
import { PinCaption } from './PinCaption';

type Props = {
   map: mapboxgl.Map | null;
   anchor: MapAnchor | null;
   /** Brings the anchor into view (the off-screen chip's action). */
   onRecenter: () => void;
   /** Map areas covered by overlays (toolbar, mobile card strip); the chip stays clear of them. */
   padding?: { top: number; right: number; bottom: number; left: number };
   onPinClick?: () => void;
};

/** Inset of the off-screen chip from the map edge, px. */
const EDGE = 16;

/** The flag graphic's height at its default size (40 wide, 48 tall), plus a hair of air. */
const CAPTION_LIFT = 50;

export const EventPinGraphic = ({ size = 40 }: { size?: number }) => (
   <svg width={size} height={(size * 48) / 40} viewBox="0 0 40 48" aria-hidden="true">
      <path d="M20 47C20 47 37 30.5 37 18.5C37 9.11 29.39 1.5 20 1.5C10.61 1.5 3 9.11 3 18.5C3 30.5 20 47 20 47Z" fill="#B91C1C" stroke="#fff" strokeWidth="3" />
      {/* Flag: reads as "the event", distinct from any hotel pin. */}
      <path d="M14 11V27" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M14.5 11.5H26L23 15.5L26 19.5H14.5V11.5Z" fill="#fff" />
   </svg>
);

/**
 * NPT-2067 — the event (or searched place) on the map: a distinct red flag pin above every hotel
 * pin, a hover/focus label, and an edge chip pointing to it when it is out of view. NPT-2264: a
 * caption names it at all times; the hover/focus label replaces it with the full description. The
 * caption is its own marker at the bottom of the stack, so hotel price pins around the search
 * centre draw over it instead of being hidden behind it.
 */
const NO_PADDING = { top: 0, right: 0, bottom: 0, left: 0 };

const EventAnchorLayer = ({ map, anchor, onRecenter, onPinClick, padding = NO_PADDING }: Props) => {
   const formatDistance = useFormatDistance();
   const { t } = useTranslation();
   const [host, setHost] = useState<HTMLDivElement | null>(null);
   const [captionHost, setCaptionHost] = useState<HTMLDivElement | null>(null);
   const [showLabel, setShowLabel] = useState(false);
   const [chip, setChip] = useState<{ x: number; y: number; tx: number; ty: number; angle: number; miles: number } | null>(null);
   const markerRef = useRef<mapboxgl.Marker | null>(null);
   const paddingRef = useRef(padding);
   paddingRef.current = padding;

   const label = anchor ? (anchor.isEvent ? t('hotel.mapView.event.pinLabel', { eventName: anchor.label }) : anchor.label) : '';

   useEffect(() => {
      if (!map || !anchor) return;
      const el = document.createElement('div');
      el.style.zIndex = '4000';
      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' }).setLngLat([anchor.longitude, anchor.latitude]).addTo(map);
      markerRef.current = marker;
      setHost(el);
      // Below every price pill (their z-index starts at 1), above the map itself.
      const captionEl = document.createElement('div');
      captionEl.style.zIndex = '1';
      captionEl.style.pointerEvents = 'none';
      const captionMarker = new mapboxgl.Marker({ element: captionEl, anchor: 'bottom', offset: [0, -CAPTION_LIFT] })
         .setLngLat([anchor.longitude, anchor.latitude])
         .addTo(map);
      setCaptionHost(captionEl);
      return () => {
         marker.remove();
         captionMarker.remove();
         markerRef.current = null;
         setHost(null);
         setCaptionHost(null);
      };
   }, [map, anchor?.latitude, anchor?.longitude]);

   // Off-screen chip: where the ray from the map centre to the anchor leaves the visible area.
   useEffect(() => {
      if (!map || !anchor) {
         setChip(null);
         return;
      }
      let frame = 0;
      const update = () => {
         frame = 0;
         const c = map.getContainer();
         const pad = paddingRef.current;
         const w = c.clientWidth;
         const h = c.clientHeight;
         const p = map.project([anchor.longitude, anchor.latitude]);
         const minX = pad.left + EDGE;
         const maxX = w - pad.right - EDGE;
         const minY = pad.top + EDGE;
         const maxY = h - pad.bottom - EDGE;
         const inside = p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;
         if (inside) {
            setChip(null);
            return;
         }
         const cx = (minX + maxX) / 2;
         const cy = (minY + maxY) / 2;
         const dx = p.x - cx;
         const dy = p.y - cy;
         const scale = Math.min(dx !== 0 ? (dx > 0 ? maxX - cx : minX - cx) / dx : Infinity, dy !== 0 ? (dy > 0 ? maxY - cy : minY - cy) / dy : Infinity);
         const center = map.getCenter();
         const x = cx + dx * scale;
         const y = cy + dy * scale;
         setChip({
            x,
            y,
            // Align the chip inward from the edge it sits on, so it is never cut off.
            tx: x >= maxX - 1 ? -100 : x <= minX + 1 ? 0 : -50,
            ty: y >= maxY - 1 ? -100 : y <= minY + 1 ? 0 : -50,
            angle: (Math.atan2(dy, dx) * 180) / Math.PI,
            miles: milesBetween({ latitude: center.lat, longitude: center.lng }, anchor),
         });
      };
      const schedule = () => {
         if (!frame) frame = requestAnimationFrame(update);
      };
      schedule();
      map.on('move', schedule);
      map.on('resize', schedule);
      return () => {
         cancelAnimationFrame(frame);
         map.off('move', schedule);
         map.off('resize', schedule);
      };
   }, [map, anchor?.latitude, anchor?.longitude, padding.top, padding.bottom, padding.left, padding.right]);

   if (!anchor) return null;

   const pin = host
      ? createPortal(
           <button
              type="button"
              className="npt-event-pin"
              aria-label={label}
              onMouseEnter={() => setShowLabel(true)}
              onMouseLeave={() => setShowLabel(false)}
              onFocus={() => setShowLabel(true)}
              onBlur={() => setShowLabel(false)}
              onClick={e => {
                 e.stopPropagation();
                 setShowLabel(s => !s);
                 onPinClick?.();
              }}
           >
              {showLabel && (
                 <span
                    role="tooltip"
                    className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-semibold text-white shadow-lg"
                 >
                    {label}
                 </span>
              )}
              <EventPinGraphic />
           </button>,
           host,
        )
      : null;

   // The hover/focus label says it all; the caption steps aside while it shows.
   const caption = captionHost && !showLabel ? createPortal(<PinCaption text={anchorCaption(anchor, t)} />, captionHost) : null;

   return (
      <>
         {pin}
         {caption}
         {chip && (
            <button
               type="button"
               onClick={onRecenter}
               className="absolute z-30 flex items-center gap-1.5 rounded-full bg-white pl-1.5 pr-3 py-1 text-xs font-semibold text-gray-900 shadow-lg ring-1 ring-black/10 hover:bg-gray-50 max-w-[70%]"
               style={{ left: chip.x, top: chip.y, transform: `translate(${chip.tx}%, ${chip.ty}%)` }}
               aria-label={t('hotel.mapView.event.offscreenAria', { name: anchor.label, distance: formatDistance(chip.miles, { style: 'long' }) })}
            >
               <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-700 text-white" aria-hidden="true">
                  <svg width="10" height="10" viewBox="0 0 10 10" style={{ transform: `rotate(${chip.angle}deg)` }}>
                     <path d="M1 5H9M9 5L5.5 1.5M9 5L5.5 8.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
               </span>
               <span className="truncate">{t('hotel.mapView.event.offscreen', { name: anchor.label, distance: formatDistance(chip.miles) })}</span>
            </button>
         )}
      </>
   );
};

export default EventAnchorLayer;
